import { useState, useRef } from 'react';
import { Button } from './button';
import { Mic, Square, Loader2, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Reusable voice note input with transcription + AI field extraction.
 * 
 * Props:
 * - endpoint: string — backend POST endpoint (receives multipart file + context form field)
 * - context: object — extra JSON context sent alongside audio (e.g., existing form data, form_type)
 * - onResult: (extracted, transcript) => void — called with AI-extracted fields + raw transcript
 * - label?: string — button label (default: "Voice Note")
 * - className?: string
 */
export function VoiceNoteInput({ endpoint, context = {}, onResult, label = "Voice Note", className = "" }) {
  const [recording, setRecording] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [transcript, setTranscript] = useState('');
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream, { mimeType: 'audio/webm;codecs=opus', audioBitsPerSecond: 32000 });
      chunksRef.current = [];
      recorder.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      recorder.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        if (blob.size < 100) { toast.error('Recording too short'); return; }
        setProcessing(true);
        try {
          const formData = new FormData();
          formData.append('file', blob, 'voice_note.webm');
          formData.append('context', JSON.stringify(context));
          const axios = (await import('axios')).default;
          const { data } = await axios.post(`${API_URL}${endpoint}`, formData, {
            withCredentials: true,
            headers: { 'Content-Type': 'multipart/form-data' },
          });
          if (data.transcript) setTranscript(data.transcript);
          if (data.extracted && onResult) {
            onResult(data.extracted, data.transcript || '');
            toast.success('Voice note processed — fields updated');
          } else if (data.error) {
            toast.error(data.error);
          }
        } catch (err) {
          toast.error('Voice processing failed');
        } finally {
          setProcessing(false);
        }
      };
      recorder.start();
      recorderRef.current = recorder;
      setRecording(true);
      setTranscript('');
    } catch {
      toast.error('Microphone access denied');
    }
  };

  const stopRecording = () => {
    try { recorderRef.current?.stop(); } catch { /* ok */ }
    setRecording(false);
  };

  return (
    <div className={`rounded-xl border border-dashed border-blue-200 bg-gradient-to-r from-blue-50/50 to-indigo-50/30 p-3 ${className}`} data-testid="voice-note-input">
      <div className="flex items-center gap-2 flex-wrap">
        <Sparkles className="w-3.5 h-3.5 text-blue-500 shrink-0" />
        <span className="text-xs font-medium text-slate-700 flex-1">Speak to auto-fill fields</span>
        {recording ? (
          <Button onClick={stopRecording} size="sm" className="rounded-full gap-1.5 text-xs h-8 bg-red-500 hover:bg-red-600 animate-pulse min-h-[44px] min-w-[44px]" data-testid="stop-voice-btn">
            <Square className="w-3 h-3 fill-white" /> Stop
          </Button>
        ) : processing ? (
          <Button disabled size="sm" className="rounded-full gap-1.5 text-xs h-8 min-h-[44px]" data-testid="voice-processing-btn">
            <Loader2 className="w-3 h-3 animate-spin" /> Processing...
          </Button>
        ) : (
          <Button onClick={startRecording} size="sm" variant="outline" className="rounded-full gap-1.5 text-xs h-8 border-blue-300 text-blue-700 hover:bg-blue-50 active:scale-95 min-h-[44px] min-w-[44px]" data-testid="start-voice-btn">
            <Mic className="w-3.5 h-3.5" /> {label}
          </Button>
        )}
      </div>
      {transcript && (
        <div className="mt-2 p-2 rounded-lg bg-white/60 border border-blue-100">
          <p className="text-[10px] font-medium text-blue-600 mb-0.5">Transcript</p>
          <p className="text-[11px] text-slate-600 italic leading-relaxed">&ldquo;{transcript}&rdquo;</p>
        </div>
      )}
    </div>
  );
}
